import { useEffect, useState } from "react";
import Footer from "../../Footer";
import Navbar from "../../Navbar";
import axios from "axios";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { formatSalesValue } from "../../../utils/formatUtils";
import * as XLSX from 'xlsx';
import { useNavigate } from "react-router-dom";
import InventoryHistoryTable from "./InventoryHistoryTable";
// import InventoryHistoryChart from "./InventoryHistoryChart";
import OutletSearch from "../../SelectSearch/OutletSearch";

const InventoryHistory = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem('token');
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const navigate = useNavigate('');

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [reportDetails, setReportDetails] = useState([]);
    const [error, setError] = useState(null);
    const [expandedReports, setExpandedReports] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState({});

    useEffect(() => {
        if (token && selectedOutlet) {
            fetchReportData();
        }
    }, [token, selectedOutlet]);

    const fetchReportData = async () => {
        try {
            setLoading(true);

            const response = await axios.post(`${baseUrl}/inventory-snapshot-food-beverage`, {
                token: token,
                outlet: selectedOutlet
            });

            const data = response.data;
            setReportData(data);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to fetch report data");
            setReportData([]);
            toast.error("Failed to fetch report data");
        } finally {
            setLoading(false);
        }
    };

    const fetchReportDetails = async (report) => {
        const key = getReportKey(report);

        try {
            setLoadingDetails(prev => ({ ...prev, [key]: true }));

            const response = await axios.post(`${baseUrl}/fnb-costing-reports`, {
                token,
                outlet: selectedOutlet,
                start_date: formatDateSimple(report.start_date),
                end_date: formatDateSimple(report.end_date)
            });

            const data = response.data;
            setReportDetails(prev => ({ ...prev, [key]: data }));

        } catch (err) {
            setReportDetails(prev => ({ ...prev, [key]: null }));
            toast.error("Failed to fetch report details");
        } finally {
            setLoadingDetails(prev => ({ ...prev, [key]: false }));
        }
    };

    const getReportKey = (report) => {
        return `${formatDateSimple(report.start_date)}_${formatDateSimple(report.end_date)}`;
    };

    const toggleReportExpansion = (report) => {
        const key = getReportKey(report);

        if (expandedReports.includes(key)) {
            setExpandedReports(prev => prev.filter(k => k !== key));
        } else {
            setExpandedReports(prev => [...prev, key]);

            if (!reportDetails[key]) {
                fetchReportDetails(report);
            }
        }
    };

    const formattedDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);

        const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
        const day = date.getDate();
        const getOrdinal = (n) => {
            if (n > 3 && n < 21) return `${n}th`;
            switch (n % 10) {
                case 1: return `${n}st`;
                case 2: return `${n}nd`;
                case 3: return `${n}rd`;
                default: return `${n}th`;
            }
        };
        const dayWithOrdinal = getOrdinal(day);
        const month = date.toLocaleDateString("en-US", { month: "long" });
        const year = date.getFullYear();

        return `${weekday}, ${dayWithOrdinal} ${month}, ${year}`;
    };

    const formatDateSimple = (dateString) => {
        if (!dateString) return "";

        const date = new Date(dateString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const handleCostingNavigation = (report) => {
        const reportKey = getReportKey(report);

        const details = reportDetails[reportKey];

        navigate('/costinganalysis', {
            state: {
                reportDetails: details
            }
        });
    };

    const groupItemsByType = (items = []) => {
        return items.reduce((acc, item) => {
            const type = item.type || "Other";
            if (!acc[type]) acc[type] = [];
            acc[type].push(item);
            return acc;
        }, {});
    };

    const handleExport = (report) => {
        const workbook = XLSX.utils.book_new();

        const reportKey = `${formatDateSimple(report.start_date)}_${formatDateSimple(report.end_date)}`;
        const details = reportDetails[reportKey];

        if (!details || !details.fnb_costing_reports?.length) {
            toast.error("No details available for export");
            return;
        }

        // Group items directly off each report by report_for, without ever
        // touching item_type, so exported sheets match on-screen totals.
        const groupedItemsByType = details.fnb_costing_reports.reduce((acc, r) => {
            const type = r.report_for || "Other";
            acc[type] = r.items_actual_costing || [];
            return acc;
        }, {});

        const formattedMeta = [
            [`Inventory Record From ${formattedDate(report.start_date)} to ${formattedDate(report.end_date)}`],
            [],
            ['Created At:', formattedDate(report.food_created_at || report.beverage_created_at)],
            ['Start Date:', formattedDate(report.start_date)],
            ['End Date:', formattedDate(report.end_date)],
            ['Food Sold:', report.food_total_items || 0],
            ['Food Accuracy:', `${report.food_accuracy || 0}%`],
            ['Beverage Sold:', report.beverage_total_items || 0],
            ['Beverage Accuracy:', `${report.beverage_accuracy || 0}%`],
            [],
        ];

        const headers = [
            "Group",
            "Item Name",
            "UOM",
            "Opening",
            "Received",
            "Wastage",
            "Consumption",
            "Closing",
            "Discrepancy",
            "Closing Amount"
        ];

        const colWidths = [
            { wch: 15 },
            { wch: 30 },
            { wch: 10 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 15 },
        ];

        Object.entries(groupedItemsByType).forEach(([type, items]) => {
            if (!items?.length) return;

            const dataRows = items.map(item => [
                item.group_name,
                item.item_name,
                item.uom,
                item.opening_count,
                item.total_received,
                item.wastage_count,
                item.total_consumed,
                item.closing_balance,
                item.discrepancy_count,
                formatSalesValue(item.physical_cost),
            ]);

            const sheetData = [...formattedMeta, headers, ...dataRows];
            const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

            worksheet['!cols'] = colWidths;

            XLSX.utils.book_append_sheet(workbook, worksheet, type);
        });

        XLSX.writeFile(
            workbook,
            `Inventory_Report_${formattedDate(report.start_date)}_to_${formattedDate(report.end_date)}.xlsx`
        );
    };

    return (
        <div className='flex flex-col min-h-screen'>
            <Navbar />

            <div className="flex-grow">
                <div className="px-12 pt-3 mb-5">
                    <div className="flex justify-center mb-3">
                        <OutletSearch
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                            token={token}
                            compact={true}
                        />
                    </div>

                    {loading && (
                        <div className="text-center my-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-danger text-center">
                            {error}
                        </div>
                    )}

                    <InventoryHistoryTable
                        reportData={reportData}
                        reportDetails={reportDetails}
                        loading={loading}
                        loadingDetails={loadingDetails}
                        expandedReports={expandedReports}
                        groupItemsByType={groupItemsByType}
                        toggleReportExpansion={toggleReportExpansion}
                        formattedDate={formattedDate}
                        handleCostingNavigation={handleCostingNavigation}
                        handleExport={handleExport}
                        token={token}
                        fetchReportData={fetchReportData}
                        setExpandedReports={setExpandedReports}
                        setReportDetails={setReportDetails}
                        getReportKey={getReportKey}
                    />

                    {/* <InventoryHistoryChart outlet={selectedOutlet} /> */}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default InventoryHistory;